/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';

import PageNavigation from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import type { NavigationLink } from 'bundles/unified-description-page-common/components/shared/PageNavigation';
import { FIXED_PAGE_NAVIGATION_Z_INDEX } from 'bundles/unified-description-page-common/constants';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

const animateIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-200px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

type Props = {
  productTitle: string;
  navigationLinks?: Array<NavigationLink>;
  pageCta?: React.ReactNode;
  partnerLogo?: React.ReactNode;
};

const styles = {
  navContainer: css`
    position: fixed;
    background: var(--cds-color-neutral-background-primary);
    width: 100%;
    opacity: 0;
    transform: translateY(-200px);
    z-index: ${FIXED_PAGE_NAVIGATION_Z_INDEX};
    top: 0;
    display: none;
    box-shadow: 0 2px 12px rgb(0 0 0 / 24%);
  `,
  navContainerWithMetaNavPadding: css`
    padding: 14px 0;
  `,
  slideIn: css`
    ${breakpoints.up('md')} {
      display: block;
    }

    animation: 0.5s ease-in-out 1 normal forwards running ${animateIn};
  `,
  header: (enableXDPRedesign: boolean) => css`
    ${enableXDPRedesign ? xdpStyles.border('border-bottom') : 'border-bottom: 2px solid var(--cds-color-grey-50);'}
    padding: var(--cds-spacing-100) 0;
    display: flex;
    gap: var(--cds-spacing-400);
    justify-content: space-between;
    align-items: center;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  headerWithMetaNavPadding: css`
    border-bottom: 0;
  `,
  titleContainer: css`
    display: flex;
    gap: var(--cds-spacing-300);
    align-items: center;
    flex: 1;
    min-width: 0;
  `,
  title: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  pageCtaContainer: (showPageCtaInNav: boolean) => css`
    visibility: ${showPageCtaInNav ? 'visible' : 'hidden'};
    opacity: ${showPageCtaInNav ? 1 : 0};
    transition: opacity 0.3s linear;
    min-width: 180px;
  `,
};

const FixedPageNavigation = ({ productTitle, navigationLinks, pageCta, partnerLogo }: Props) => {
  const { showFixedPageNav, showPageCtaInNav, updateFixedPageNavHeight } = usePageNavigation();
  const navRef = useRef<HTMLDivElement | null>(null);
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  useEffect(() => {
    if (showFixedPageNav) {
      const navHeight = navRef.current?.getBoundingClientRect().height;

      if (navHeight && updateFixedPageNavHeight) {
        updateFixedPageNavHeight(navHeight);
      }
    }
  }, [updateFixedPageNavHeight, showFixedPageNav]);

  if (!productTitle) {
    return null;
  }

  return (
    <div
      data-e2e="fixed-page-navigation"
      css={[
        styles.navContainer,
        showFixedPageNav ? styles.slideIn : undefined,
        enableXDPRedesign ? styles.navContainerWithMetaNavPadding : undefined,
      ]}
      ref={navRef}
    >
      <PageGridContainer>
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <div
              css={[styles.header(enableXDPRedesign), enableXDPRedesign ? styles.headerWithMetaNavPadding : undefined]}
            >
              <div css={styles.titleContainer}>
                {partnerLogo}
                <Typography2 variant="subtitleMedium" css={styles.title} component="h2">
                  {productTitle}
                </Typography2>
              </div>
              {showPageCtaInNav && <div css={styles.pageCtaContainer(showPageCtaInNav)}>{pageCta}</div>}
            </div>
            {!enableXDPRedesign && <PageNavigation navigationLinks={navigationLinks} />}
          </Grid>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default FixedPageNavigation;
